import { AlertCircle, Backpack, CalendarDays, MapPin, Navigation, Tent, UserPlus, Utensils } from '@/ui/icons'
import type { RangerBuiltTrip, RangerFunctionCallResult, RangerInviteFriendsResult } from '@/shared/api/ranger'
import { formatTripDateRange } from '@/modules/trips/utils/dateHelpers'
import { RangerBuildTripCard } from './RangerBuildTripCard'

interface DispersedCampsitesResult {
  location: string
  count: number
  results: Array<{
    roadName: string
    distanceFromLocation: number
  }>
}

interface CreatedWaypointResult {
  name: string
}

interface CreatedMealResult {
  name: string
  planned_date: string
}

interface TripUpdateResult {
  name?: string
  start_date?: string
  end_date?: string
  destination?: string | null
  description?: string
}

function describeTripUpdate({ name, start_date, end_date, destination, description }: TripUpdateResult): string {
  if (start_date) return `Dates set · ${formatTripDateRange(start_date, end_date ?? start_date)}`
  if (destination) return `Added · ${destination}`
  if (name) return `Renamed · ${name}`
  return description ? 'Description updated' : 'Trip updated'
}

const MAX_SPOTS_SHOWN = 4

function DispersedCampsitesCard({ result }: { result: DispersedCampsitesResult }) {
  if (!result.results?.length) return null
  const shown = result.results.slice(0, MAX_SPOTS_SHOWN)
  const remaining = result.count - shown.length

  return (
    <div className="w-full max-w-sm overflow-hidden rounded-lg border border-border bg-earth-50 dark:bg-earth-800/40">
      <div className="flex items-center gap-2 border-b border-border px-3 py-2">
        <Tent className="h-4 w-4 shrink-0 text-adventure-600" />
        <span className="text-xs font-medium text-foreground">
          {result.count} spot{result.count === 1 ? '' : 's'} near {result.location}
        </span>
      </div>
      <ul className="divide-y divide-border">
        {shown.map((spot, i) => (
          <li key={i} className="flex items-center justify-between gap-3 px-3 py-2 text-xs">
            <span className="flex min-w-0 items-center gap-1.5 text-foreground">
              <Navigation className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <span className="truncate">{spot.roadName}</span>
            </span>
            <span className="shrink-0 text-muted-foreground">{spot.distanceFromLocation.toFixed(1)} mi</span>
          </li>
        ))}
      </ul>
      {remaining > 0 && (
        <div className="border-t border-border px-3 py-1.5 text-center text-[11px] text-muted-foreground">
          +{remaining} more found
        </div>
      )}
    </div>
  )
}

function ResultPill({ icon: Icon, children }: { icon: typeof MapPin; children: React.ReactNode }) {
  return (
    <div className="status-success inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium">
      <Icon className="h-3.5 w-3.5" />
      {children}
    </div>
  )
}

interface RangerFunctionResultCardProps {
  call: RangerFunctionCallResult
}

export function RangerFunctionResultCard({ call }: RangerFunctionResultCardProps) {
  if (!call.success) {
    return (
      <div className="status-error inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium">
        <AlertCircle className="h-3.5 w-3.5" />
        {call.error || 'That action could not be completed.'}
      </div>
    )
  }

  switch (call.name) {
    case 'create_trip':
      return <RangerBuildTripCard trip={call.result as RangerBuiltTrip} />
    case 'update_trip':
      return <ResultPill icon={CalendarDays}>{describeTripUpdate(call.result as TripUpdateResult)}</ResultPill>
    case 'add_gear':
      return <ResultPill icon={Backpack}>Added {(call.result as { name: string }).name} to gear</ResultPill>
    case 'invite_friends': {
      const { invited } = call.result as RangerInviteFriendsResult
      if (!invited?.length) return null
      return <ResultPill icon={UserPlus}>Invited {invited.join(', ')}</ResultPill>
    }
    case 'find_dispersed_campsites':
      return <DispersedCampsitesCard result={call.result as DispersedCampsitesResult} />
    case 'create_waypoint': {
      const result = call.result as CreatedWaypointResult
      if (!result?.name) return null
      return <ResultPill icon={MapPin}>Added waypoint · {result.name}</ResultPill>
    }
    case 'create_meal': {
      const result = call.result as CreatedMealResult
      if (!result?.name) return null
      return <ResultPill icon={Utensils}>Added meal · {result.name}</ResultPill>
    }
    default:
      return null
  }
}
